import { CandidateInfoQuery, CandidateInfoQueryResult } from "@generated/graphql";
import { Box, Divider, Grid, Stack } from "@mui/material";
import FieldLabel from "components/FieldLabel";
import FileUploadButton from "components/buttons/FileUploadButton";
import SaveEditButtons from "components/buttons/SaveEditButtons";
import AutocompleteControl from "components/form/AutocompleteControl";
import Form from "components/form/Form";
import TextFieldControl from "components/form/TextFieldControl";
import { AuthSettingName } from "constants/candidate.constants";
import { CANDIDATE_TYPE_OPTIONS, GRADE_OPTIONS } from "constants/global";
import ROUTES from "constants/routes";
import typography from "constants/typography.constants";
import { ToastContext, ToastTypeEnum } from "context/toastContext";
import { parseApolloError } from "helpers/errorsParser";
import { getTelegramUserName } from "helpers/form.helpers";
import VALIDATION from "helpers/validation";
import useIsCreate from "hooks/useIsCreate";
import useUpdateCandidate from "hooks/useUpdateCandidate";
import { useContext, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { CandidateInputs } from "../types";
import AuthSettings from "./AuthSettings";
import VacanciesList from "./VacanciesList";

interface ContentProps {
  refetch: CandidateInfoQueryResult["refetch"];
  companyCandidate: CandidateInfoQuery["companyCandidate"];
}

const Content = ({ refetch, companyCandidate }: ContentProps) => {
  const { addToast } = useContext(ToastContext);
  const isCreate = useIsCreate();
  const [isEditing, setIsEditing] = useState<boolean>(isCreate);
  const navigate = useNavigate();
  const { email } = companyCandidate || {};
  const companyCandidateLogin = companyCandidate?.candidateUsername?.login;

  const { onUpdateCompanyCandidate, updateCompanyCandidateLoading } = useUpdateCandidate();

  const onSubmit = (formData: CandidateInputs) => {
    const hasEnabledAuthSetting = Object.values(formData.authSettingsSwitch || {}).some(Boolean);
    if (!hasEnabledAuthSetting) {
      addToast({
        type: ToastTypeEnum.ERROR,
        text: "Необходимо включить хотя бы один способ авторизации",
      });
      return;
    }

    const updatePromise = onUpdateCompanyCandidate(formData);
    if (!updatePromise) return;

    updatePromise
      .then(({ data }) => {
        addToast({ type: ToastTypeEnum.SUCCESS });
        setIsEditing(false);

        const candidateId = data?.createOrUpdateCompanyCandidate.id;

        if (candidateId) {
          if (isCreate) {
            navigate(`/${ROUTES.CANDIDATE}/view/${candidateId}`, { replace: true });
          } else {
            refetch();
          }
        }
      })
      .catch((e) => parseApolloError(e, { addToast, defaultText: "Ошибка сохранения кандидата" }));
  };

  const form = useForm<CandidateInputs>({
    values: {
      id: companyCandidate?.id,
      telegramUsername: getTelegramUserName(companyCandidate?.candidateUsername),
      fullName: companyCandidate?.fullName || "",
      email: companyCandidate?.candidateUsername?.email || "",
      yandexEmail: companyCandidate?.candidateUsername?.yandexEmail,
      vkId: companyCandidate?.candidateUsername?.vkId,
      workEmail: companyCandidate?.email || "",
      phoneNumber: companyCandidate?.candidateUsername?.candidate?.phoneNumber?.replace("+", ""),
      grade: GRADE_OPTIONS.find((grade) => grade.id === companyCandidate?.grade) || null,
      organization: companyCandidate?.organization || "",
      position: companyCandidate?.position || "",
      type: CANDIDATE_TYPE_OPTIONS.find((type) => type.id === companyCandidate?.type) || null,
      login: companyCandidateLogin || "",
      link: companyCandidate?.link || "",
      password: "",
      cvFile: companyCandidate?.cvFile,
      authSettingsSwitch: {
        [AuthSettingName.EMAIL]: !!companyCandidate?.candidateUsername?.email,
        [AuthSettingName.YANDEX_ID]: !!companyCandidate?.candidateUsername?.yandexEmail,
        [AuthSettingName.LOGIN]: !!companyCandidateLogin,
        [AuthSettingName.VK_ID]: !!companyCandidate?.candidateUsername?.vkId,
        [AuthSettingName.TELEGRAM]: !!companyCandidate?.candidateUsername?.telegramUsername,
      },
    },
  });
  const { handleSubmit } = form;

  return (
    <Box
      sx={{
        backgroundColor: "white",
        borderRadius: 2,
        p: 2.5,
      }}>
      <Form form={form}>
        <Grid
          container
          spacing={2.5}>
          <Grid
            item
            xs={12}
            md={6}>
            <Stack spacing={2}>
              <TextFieldControl
                name='fullName'
                label='ФИО'
                disabled={!isEditing}
                rules={{ required: true, ...VALIDATION.fullUserName }}
              />
              <TextFieldControl
                name='workEmail'
                label='Рабочий Email'
                placeholder='Почта'
                disabled={!isEditing}
                rules={VALIDATION.emailFull}
                labelLink={email ? { to: `mailto:${email}`, target: "_blank", text: typography.write } : undefined}
              />
              <TextFieldControl
                name='phoneNumber'
                label='Номер телефона'
                disabled
                maskProps={{ mask: "+{7} (000) 000-00-00" }}
              />
              <Divider />
              <AuthSettings
                companyCandidate={companyCandidate}
                isEditing={isEditing}
              />
            </Stack>
          </Grid>
          <Grid
            item
            xs={12}
            md={6}>
            <Stack spacing={2}>
              <AutocompleteControl
                options={GRADE_OPTIONS}
                name='grade'
                label='Грейд'
                placeholder='Грейд'
                disabled={!isEditing}
              />
              <TextFieldControl
                name='organization'
                label='Организация'
                placeholder='Организация'
                disabled={!isEditing}
                rules={{ ...VALIDATION.companyName }}
              />
              <TextFieldControl
                name='position'
                label='Должность'
                placeholder='Должность'
                disabled={!isEditing}
                rules={{ ...VALIDATION.position }}
              />
              <AutocompleteControl
                options={CANDIDATE_TYPE_OPTIONS}
                name='type'
                label='Тип'
                placeholder='Тип'
                disabled={!isEditing}
                rules={{ required: true }}
              />
              <TextFieldControl
                name='link'
                label='Ссылка на HH'
                placeholder='Ссылка на HH'
                disabled={!isEditing}
                rules={{ ...VALIDATION.position }}
              />
              <Stack spacing={0.5}>
                <FieldLabel label='Резюме' />
                <FileUploadButton
                  name='cvFile'
                  label='Загрузить резюме'
                  fileUrl={companyCandidate?.cvFile?.url}
                  disabled={!isEditing}
                  accept='application/pdf'
                  tooltipText='Отчет по CV'
                />
              </Stack>
            </Stack>
          </Grid>
          <VacanciesList isEditing={isEditing} />
          <Grid
            item
            xs={12}
            md={3}>
            <SaveEditButtons
              isEdited={isEditing}
              onSubmit={handleSubmit(onSubmit)}
              setIsEdited={setIsEditing}
              loading={updateCompanyCandidateLoading}
            />
          </Grid>
        </Grid>
      </Form>
    </Box>
  );
};

export default Content;
